import { apiClient } from '@app/lib'
import { useSugarContext } from '@app/providers'
import { type SugarFamilyLettersResponse } from '@app/types'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useSession } from 'next-auth/react'

export const useFamilyLetters = ({ programYearId }: { programYearId: string | null | undefined }) => {
  const session = useSession()
  const { siteId } = useSugarContext()

  return useQuery({
    // programYearId is optional per MIQ-2393 — server resolves the active PY when omitted.
    // Only siteId is required to fire the request.
    enabled: session.status === 'authenticated' && Boolean(siteId),
    placeholderData: keepPreviousData,
    queryKey: ['family-letters', { programYearId, siteId }],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<SugarFamilyLettersResponse>('/api/family-letters', {
        params: {
          programYearId: programYearId ?? undefined,
          siteId
        },
        signal
      })

      // Sugar emits HTTP 200 with `success: false` for app-level failures (formatErrorResponse).
      // Surface those through React Query's error state instead of letting `data.length` crash.
      if (!data.success) {
        throw new Error(data.messages?.[0]?.message ?? 'Unable to retrieve family letters at this time.')
      }

      return data
    }
  })
}
